import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Typography, Button } from '@mui/material';
import * as Sentry from '@sentry/react';
import carbonCareTheme from './theme/carbonCareTheme';
import { usePushNotifications } from './hooks/usePushNotifications';



// Layout Components
import DashboardLayout from './components/layout/DashboardLayout';
import FeedbackDialog from './components/common/FeedbackDialog';
import ProtectedRoute from './components/layout/ProtectedRoute';

// Pages
import AuthPage from './pages/AuthPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import DashboardPage from './pages/DashboardPage';
import EmissionsDashboard from './pages/Dashboard';
import CompaniesPage from './pages/CompaniesPage';
import UsersPage from './pages/UsersPage';
import ExcelUploadPage from './pages/ExcelUploadPage';
import UploadedFilesPage from './pages/UploadedFilesPage';
import ProcessedFilesPage from './pages/ProcessedFilesPage';
import CalculatedFilesPage from './pages/CalculatedFilesPage';
import FinalFilesPage from './pages/FinalFilesPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import XmlApiPage from './pages/XmlApiPage';
import ApiDocsPage from './pages/ApiDocsPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import AnalyticsPage from './pages/Analytics';
import CertificatesPage from './pages/CertificatesPage';
import ShipmentsPage from './pages/ShipmentsPage';
import AdminUpdatesPage from './pages/AdminUpdatesPage';
import ReportSchedulerPage from './pages/ReportSchedulerPage';
import LibraryPage from './pages/LibraryPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import LibraryFilesPage from './pages/LibraryFilesPage';
import FaqPage from './pages/FaqPage';
// Data Management Pages
import AirportsPage from './pages/dataManagement/AirportsPage';
import SeaportsPage from './pages/dataManagement/SeaportsPage';
import PostalCodesPage from './pages/dataManagement/PostalCodesPage';
import DistanceCachePage from './pages/dataManagement/DistanceCachePage';
import CitiesPage from './pages/dataManagement/CitiesPage';
import AddressesPage from './pages/dataManagement/AddressesPage';

// Statistics & GHG Reporting Pages
import StatisticsOverviewPage from './pages/StatisticsOverviewPage';
import CompanyStatisticsPage from './pages/CompanyStatisticsPage';
import GHGOverviewPage from './pages/GHGOverviewPage';
import CompanyGHGPage from './pages/CompanyGHGPage';
import AuditLogsPage from './pages/AuditLogsPage';

// Types
import { AccountType } from './types/auth';
import { useAuthStore } from './store/authStore';
import { companyApi } from './services/api/companyApi';

/**
 * Guards routes based on company feature flags (analytics, certificateAvailable).
 * Redirects to /dashboard or /unauthorized if the feature is disabled for the user's company.
 */
// Maps camelCase feature names to the snake_case keys returned by the API
const featureKeyMap: Record<string, string> = {
  analytics: 'analytics',
  certificateAvailable: 'certificate_available',
};

const CompanyFeatureGuard: React.FC<{
  requiredFeature: 'analytics' | 'certificateAvailable';
  children: React.ReactNode;
}> = ({ requiredFeature, children }) => {
  const { user } = useAuthStore();
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    const checkFeature = async () => {
      if (!user?.companyId) {
        setAllowed(false);
        return;
      }
      // CarbonCareAdmin and MasterAdmin bypass company feature checks
      if (user.accountType >= AccountType.CarbonCareAdmin) {
        setAllowed(true);
        return;
      }
      try {
        const company: any = await companyApi.getById(user.companyId);
        // Check both snake_case (raw API) and camelCase keys
        const snakeKey = featureKeyMap[requiredFeature];
        const value = company[requiredFeature] ?? company[snakeKey];
        setAllowed(value === true);
      } catch {
        setAllowed(false);
      }
    };
    checkFeature();
  }, [user?.companyId, requiredFeature, user?.accountType]);

  if (allowed === null) return null; // loading
  if (!allowed) return <Navigate to="/unauthorized" replace />;
  return <>{children}</>;
};

/**
 * Mounts the push-notification poller for the entire app session.
 * Without this the poller only runs while SettingsPage is open.
 */
const PushNotificationPoller: React.FC = () => {
  usePushNotifications();
  return null;
};

function ErrorFallback({ eventId }: Readonly<{ eventId: string | null }>) {
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 4, textAlign: 'center' }}>
      <Typography variant="h4" gutterBottom>Something went wrong</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
        An unexpected error occurred. The crash has been automatically reported to our team.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Help us fix it faster by adding more details.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button variant="outlined" onClick={() => setDialogOpen(true)}>
          Add Details to Report
        </Button>
        <Button variant="contained" onClick={() => globalThis.location.href = '/dashboard'}>
          Return to Dashboard
        </Button>
      </Box>
      <FeedbackDialog
        open={dialogOpen}
        defaultMode="bug"
        associatedEventId={eventId}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={carbonCareTheme}>
      <CssBaseline />
      {/* Keeps the browser-notification poller alive across all pages */}
      <PushNotificationPoller />
      <Router>
        <Sentry.ErrorBoundary fallback={ErrorFallback}>
          <Routes>
            {/* Public routes */}
            <Route path="/auth/login" element={<AuthPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/auth/signup" element={<AuthPage />} />
            <Route path="/password-reset" element={<AuthPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/set-password" element={<AuthPage />} />
            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requiredRole={AccountType.SubCompanyAdmin}>
                  <DashboardLayout>
                    <DashboardPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/emissions-dashboard"
              element={
                <ProtectedRoute>
                  <CompanyFeatureGuard requiredFeature="analytics">
                    <EmissionsDashboard />
                  </CompanyFeatureGuard>
                </ProtectedRoute>
              }
            />

            <Route
              path="/companies"
              element={
                <ProtectedRoute requiredRole={AccountType.SubCompanyAdmin}>
                  <DashboardLayout>
                    <CompaniesPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* <Route
              path="/hierarchy"
              element={
                <ProtectedRoute requiredRole={AccountType.User}>
                  <HierarchyVisualizationPage />
                </ProtectedRoute>
              }
            /> */}

            <Route
              path="/users"
              element={
                <ProtectedRoute requiredRole={AccountType.LicenseeAdmin}>
                  <DashboardLayout>
                    <UsersPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/excel-upload"
              element={
                <ProtectedRoute requiredRole={AccountType.LicenseeAdmin}>
                  <DashboardLayout>
                    <ExcelUploadPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/files/uploaded"
              element={
                <ProtectedRoute requiredRole={AccountType.User}>
                  <DashboardLayout>
                    <UploadedFilesPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/files/processing"
              element={
                <ProtectedRoute requiredRole={AccountType.CarbonCareAdmin}>
                  <DashboardLayout>
                    <ProcessedFilesPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/files/calculated"
              element={
                <ProtectedRoute requiredRole={AccountType.CarbonCareAdmin}>
                  <DashboardLayout>
                    <CalculatedFilesPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/files/final"
              element={
                <ProtectedRoute requiredRole={AccountType.User}>
                  <DashboardLayout>
                    <FinalFilesPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/xml-api"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <XmlApiPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/api-docs"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ApiDocsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <CompanyFeatureGuard requiredFeature="analytics">
                    <AnalyticsPage />
                  </CompanyFeatureGuard>
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ProfilePage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <SettingsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/certificates"
              element={
                <ProtectedRoute requiredRole={AccountType.SubCompanyAdmin}>
                  <CompanyFeatureGuard requiredFeature="certificateAvailable">
                    <DashboardLayout>
                      <CertificatesPage />
                    </DashboardLayout>
                  </CompanyFeatureGuard>
                </ProtectedRoute>
              }
            />

            <Route
              path="/shipments"
              element={
                <ProtectedRoute requiredRole={AccountType.User}>
                  <DashboardLayout>
                    <ShipmentsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* Statistics Routes */}
            <Route
              path="/statistics/overview"
              element={
                <ProtectedRoute requiredRole={AccountType.CarbonCareAdmin}>
                  <DashboardLayout>
                    <StatisticsOverviewPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/statistics/company/:companyId?"
              element={
                <ProtectedRoute requiredRole={AccountType.LicenseeAdmin}>
                  <DashboardLayout>
                    <CompanyStatisticsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* GHG Reporting Routes */}
            <Route
              path="/ghg-reporting/overview"
              element={
                <ProtectedRoute requiredRole={AccountType.CarbonCareAdmin}>
                  <DashboardLayout>
                    <GHGOverviewPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/ghg-reporting/company/:companyId?"
              element={
                <ProtectedRoute requiredRole={AccountType.LicenseeAdmin}>
                  <DashboardLayout>
                    <CompanyGHGPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin/updates"
              element={
                <ProtectedRoute requiredRole={AccountType.MasterAdmin}>
                  <DashboardLayout>
                    <AdminUpdatesPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/report-scheduler"
              element={
                <ProtectedRoute requiredRole={AccountType.MasterAdmin}>
                  <DashboardLayout>
                    <ReportSchedulerPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* Data Management Routes - Master Admin Only */}
            <Route
              path="/data-management/airports"
              element={
                <ProtectedRoute requiredRole={AccountType.CarbonCareAdmin}>
                  <DashboardLayout>
                    <AirportsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/data-management/seaports"
              element={
                <ProtectedRoute requiredRole={AccountType.CarbonCareAdmin}>
                  <DashboardLayout>
                    <SeaportsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/data-management/postal-codes"
              element={
                <ProtectedRoute requiredRole={AccountType.CarbonCareAdmin}>
                  <DashboardLayout>
                    <PostalCodesPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/data-management/distance-cache"
              element={
                <ProtectedRoute requiredRole={AccountType.CarbonCareAdmin}>
                  <DashboardLayout>
                    <DistanceCachePage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/data-management/cities"
              element={
                <ProtectedRoute requiredRole={AccountType.CarbonCareAdmin}>
                  <DashboardLayout>
                    <CitiesPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/data-management/addresses"
              element={
                <ProtectedRoute requiredRole={AccountType.CarbonCareAdmin}>
                  <DashboardLayout>
                    <AddressesPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/audit-logs"
              element={
                <ProtectedRoute requiredRole={AccountType.CarbonCareAdmin}>
                  <DashboardLayout>
                    <AuditLogsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* Knowledge Base Routes */}
            <Route
              path="/knowledge-base"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <KnowledgeBasePage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/knowledge-base/library"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LibraryFilesPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/knowledge-base/faq"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <FaqPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/knowledge-base/api-docs"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ApiDocsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* Legacy Library Route – redirect to new location */}
            <Route
              path="/library"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LibraryPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* Redirect routes */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Sentry.ErrorBoundary>
      </Router>
    </ThemeProvider>
  );
}

export default App;
